import { useState } from 'react';
import type { AppPlan } from '@/webapp/plan/planTypes';

/**
 * What a visitor will actually walk through, drawn from the plan itself.
 *
 * Ported from src/components/build/JourneyDiagram.tsx. The review step lists screens as
 * rows, which says what exists but not what it FEELS like to use — and "is this the app I
 * asked for?" is a question about the journey, not the inventory.
 *
 * Everything here comes from the plan and nothing is invented. The plan records which screens
 * exist and in what order, and whether the app collects anything; it does NOT record which
 * screen collects, or which screen a chart sits on. So those are shown as facts about the app
 * rather than pinned to a node they cannot honestly be attached to. A diagram that guessed
 * would be a picture of an app nobody is building.
 */
export default function JourneyDiagram({ plan }: { plan: AppPlan }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const charts = plan.charts.filter((c) => c.included);

  return (
    <div className="journey" aria-label="What someone using your app will go through">
      <p className="type-caption journey-ends">Someone opens your link</p>

      <ol className="journey-nodes">
        {plan.screens.map((screen, i) => {
          const open = openId === screen.id;
          return (
            <li key={screen.id} className="journey-item">
              <span className="journey-connector" aria-hidden="true" />
              <button
                type="button"
                className={`card clay-card journey-node ${open ? 'is-open' : ''}`}
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : screen.id)}
              >
                <span className="journey-step">{i + 1}</span>
                <span className="journey-body">
                  <span className="type-heading">{screen.label}</span>
                  {/* Collapsed to one line so the SHAPE of the journey reads at a glance;
                      the whole purpose is there when a screen is opened. */}
                  <span className={`type-caption plan-detail ${open ? '' : 'journey-clamp'}`}>
                    {screen.purpose}
                  </span>
                </span>
                {i === 0 && <span className="type-caption journey-first">starts here</span>}
              </button>
            </li>
          );
        })}
      </ol>

      {plan.collects && (
        <>
          <span className="journey-connector" aria-hidden="true" />
          <p className="type-caption journey-collects">
            They can send you {plan.collects.fields.slice(0, 3).join(', ')}
            {plan.collects.fields.length > 3 ? `, +${plan.collects.fields.length - 3} more` : ''}
          </p>
        </>
      )}

      {charts.length > 0 && (
        <p className="type-caption plan-detail">
          {charts.length === 1 ? 'A chart' : `${charts.length} charts`} drawn from your own data:{' '}
          {charts.map((c) => c.label).join(', ')}
        </p>
      )}
    </div>
  );
}
